import React, { useContext, useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { TabPanel, Tabs, TabList, Tab } from 'react-tabs'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'
import classNames from 'classnames'

import { GlobalContext } from '../../../contexts/GlobalContext'
import { api } from '../../../services/api'
import masks from '../../../util/masks'
import scrollUtils from '../../../util/scroll'
import Spinner from '../../../components/Spinner'
import { PlanosContainer } from './styles'
import { addToCart } from '../../../util/cart'

const modulosIdsByURL = [
	{ suffix: '/prontuario', id: 1 },
	{ suffix: '/enquetes', id: 3 }
]

export default function () {
	const planosListaRef = useRef()

	const { pathname, hash } = useLocation()

	const estaAutenticado = pathname.includes('/auth/produtos/prontuario')

	const { user } = useContext(GlobalContext)

	const [listaPlanosPosicao, setListaPlanosPosicao] = useState('start')

	const { data: planos } = useQuery({
		queryKey: ['get-lista-planos'],
		queryFn: async () => {
			try {
				const moduloId = modulosIdsByURL.find(item => pathname.includes(item.suffix))?.id

				const response = await api.get('plano', {
					params: {
						modulo_id: moduloId
					}
				})

				let result = Object.groupBy(response, ({ identificador }) => {
					if (identificador.startsWith('salvavidas')) {
						return 'salvavidas'
					}

					if (identificador.startsWith('individual')) {
						return 'individual'
					}

					if (identificador.startsWith('familia')) {
						return 'familia'
					}

					if (identificador.startsWith('empresa')) {
						return 'empresa'
					}

					if (identificador.startsWith('medico')) {
						return 'medico'
					}

					throw new Error('Tipo de plano não tratado.')
				})

				result = Object.values(result)

				return result
			} catch (e) {
				console.log(e)
			}
		}
	})

	function handleScroll(direcao) {
		const lista = document.getElementById('planos-lista')

		const deslocamento = direcao === 'right' ? 318 : -318

		let novaPosicao = lista.scrollLeft + deslocamento

		if (novaPosicao < 0) {
			novaPosicao = 0
		}

		if (novaPosicao > lista.scrollWidth) {
			novaPosicao = lista.scrollWidth
		}

		lista.scrollLeft = novaPosicao

		if (novaPosicao === 0) {
			setListaPlanosPosicao('start')
		} else if (novaPosicao + lista.clientWidth === lista.scrollWidth) {
			setListaPlanosPosicao('end')
		} else {
			setListaPlanosPosicao(null)
		}
	}

	useEffect(() => {
		if (hash) {
			setTimeout(() => {
				scrollUtils.toElement(hash)
			}, 1500)
		}
	}, [hash])

	return (
		<section id="planos" className="pb-20 pt-28 px-4 bg-gray-200">
			<div className="container mx-auto">
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						Planos que se adequam a você
					</h2>
					<p className="text-lg text-gray-700 max-w-3xl mx-auto">
						Escolha o plano que melhor atende às suas necessidades e tenha acesso
						a todas as funcionalidades do Prontuário da Saúde do Cidadão.
					</p>
				</div>

				{!planos ? (
					<Spinner />
				) : (
					<PlanosContainer id="planos-lista" className="transition-all" ref={planosListaRef}>
						<ul className="items">
							{planos.map((plano, idx) => {
								const { id, valor } = plano[0]
								let link = '/signup'

								if(user) {
									if(id === 1) { // Plano grátis
										link = `/contratacao/gratis/${id}`
									} else if(user) {
										link = '/auth/checkout'
									}	
								}

								return (
									<li key={id}>
										<Plano
											planos={plano}
											preco={masks.maskApply.currency(valor / 100)}
											destaque
											botaoLink={link}
											info={idx === 0 ? `<span class='animate-pulse'>Grátis</span>` : null}
										/>
									</li>
								)
							})}
						</ul>

						{listaPlanosPosicao !== 'start' && (
							<div
								className={classNames(
									'flex absolute mt-64 bg-gray-700 w-12 h-12 justify-center items-center rounded-full p-1 cursor-pointer opacity-40 hover:opacity-70 transition-opacity',
									{
										'left-56': estaAutenticado,
										'left-4': !estaAutenticado
									})}
								onClick={() => { handleScroll('left') }}
							>
								<FiArrowLeft size={20} />
							</div>
						)}

						{listaPlanosPosicao !== 'end' && (
							<div
								className="flex absolute right-4 mt-64 bg-gray-700 w-12 h-12 justify-center items-center rounded-full p-1 cursor-pointer opacity-40 hover:opacity-70 transition-opacity"
								onClick={() => { handleScroll('right') }}
							>
								<FiArrowRight size={20} />
							</div>
						)}
					</PlanosContainer>
				)}
			</div>
		</section>
	)
}

function Plano({
	planos,
	botaoLink,
	destaque = false,
	info
}) {
	const history = useHistory()

	const [selected, setSelected] = useState(0)

	function handleRedirect(plano) {
		addToCart(plano)

		// localStorage.setItem('sigas:carrinho', JSON.stringify({
		// 	expiracao: addDays(new Date(), 3),
		// 	id: plano.id,
		// 	descricao: plano.descricao,
		// 	valor: plano.valor,
		// 	periodo: plano.periodo,
		// 	modulo: {
		// 		id: plano.modulo.id,
		// 		descricao: plano.modulo.descricao
		// 	},
		// 	identificador: plano.identificador
		// }))

		history.push(botaoLink)
	}

	const valorAnual = planos.find(p => p.periodo === 'anual')?.valor
	const valorMensal = planos.find(p => p.periodo === 'mensal')?.valor
	const percentualDesconto = 100 - Math.round((valorAnual * 100) / (valorMensal * 12))
	const temPlanoAnual = planos.some(p => p.periodo === 'anual')

	const botaoTexto = temPlanoAnual ? 'Assinar Agora' : 'Começar a usar'

	function getContent(plano, idx = 0) {
		return (
			<div className="flex flex-col">
				<span
					className={classNames(
						'uppercase font-medium mb-3 flex items-end',
						{
							'self-center text-emerald-600 text-lg': !temPlanoAnual,
							'self-end text-xs text-pink-600': temPlanoAnual
						}
					)}
					dangerouslySetInnerHTML={{
						__html: info
					}}
				/>

				<h3 className="text-2xl font-bold text-gray-900 mb-2">{plano.descricao}</h3>

				<div className="flex flex-col font-normal" style={{ fontSize: 14 }}>
					{idx === 1 && (
						<div className="flex gap-3 justify-between items-center mb-2">
							<span className="line-through">
								{masks.maskApply.currency((valorMensal * 12) / 100)}
							</span>

							<span className="bg-lime-300 p-1 rounded-md" style={{ fontSize: 12 }}>
								{percentualDesconto}% OFF
							</span>
						</div>
					)}

					{temPlanoAnual && (
						<div className="mb-4 text-slate-800">
							<span className="text-3xl font-bold">{masks.maskApply.currency(plano.valor / 100)}</span>
							{plano.valor !== "Personalizado" && <span className="text-gray-600">{plano.periodo === 'mensal' ? '/mês' : '/ano'}</span>}
						</div>
					)}
				</div>

				<p className="text-gray-700 mb-6">{plano.detalhes}</p>

				<ul className="space-y-3 mb-8">
					{plano.recursos.filter(recurso => recurso.publico).map((recurso, index) => (
						<li key={index} className="flex items-start">
							<Check size={20} className="text-emerald-500 mr-2 flex-shrink-0 mt-1" />
							<span className="text-gray-700" dangerouslySetInnerHTML={{
								__html: recurso.descricao
							}} />
						</li>
					))}
				</ul>

				{plano.detalhe_extra && <p className="text-gray-400 mb-6 text-xs mx-6">* {plano.detalhe_extra}</p>}

				<span 
					onClick={() => handleRedirect(plano)} 
					className={`
						w-full rounded-md px-4 py-3 text-center hover:font-semibold cursor-pointer
						${destaque ? 'bg-emerald-600 text-white hover:text-white hover:bg-emerald-600/90' : 'bg-white text-slate-800 border border-gray-300 hover:text-slate-800'}
					`}
				>
					{botaoTexto}
				</span>
			</div>
		)
	}

	return (
		<div className={`
			border rounded-xl p-8 flex flex-col h-fit
			${destaque ? 'border-slate-700 shadow-lg' : 'border-gray-300'}
			transition-all duration-300 hover:shadow-md bg-white
			hover:scale-105
		`}>
			{temPlanoAnual ? (
				<Tabs className="tabs-landing-page" selectedIndex={selected} onSelect={indice => setSelected(indice)}>
					<TabList>
						<Tab>Mensal</Tab>
						<Tab>
							<span>Anual</span>
							<span className="subtext text-xs ml-2 font-bold">{`${percentualDesconto}% OFF`}</span>
						</Tab>
					</TabList>

					{planos?.map((plano, idx) => (
						<TabPanel key={plano.id}>
							{getContent(plano, idx)}
						</TabPanel>
					))}
				</Tabs>
			) : getContent(planos[0])}
		</div>
	)
}